import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Logo } from '@/components/ui/Logo';
import { ArrowLeft } from 'lucide-react';
import { AnimatedLoginBackground } from '@/components/layout/AnimatedLoginBackground';
import { Button } from '@/components/ui/Button';

export const EsqueciSenha = () => {
  const [searchParams] = useSearchParams();
  const emailQuery = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailQuery);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState('');

  // Prevent scrolling on the entire page while on this route
  useEffect(() => {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
          document.body.style.overflow = originalOverflow;
      };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMensagem('');
    setErro('');

    try {
      const { data, error } = await supabase.functions.invoke('reset-password', {
        body: { email }
      });

      if (error) throw error;
      
      if (data?.error) {
        setErro(`Erro: ${data.error}`);
      } else {
        setMensagem('Se o e-mail estiver vinculado a uma conta, você receberá um link de recuperação na sua caixa de entrada em instantes.');
      }
    } catch (error: unknown) {
      const err = error as { context?: { json?: () => Promise<{ error?: string }> }; message?: string };
      if (err?.context && typeof err.context.json === 'function') {
        try {
          const body = await err.context.json();
          setErro(body?.error || err.message || 'Ocorreu um erro inesperado.');
          return;
        } catch {
          // Fallback para a mensagem original caso falhe o parse
        }
      }
      setErro(err?.message || 'Ocorreu um erro inesperado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedLoginBackground>
        <div className="flex flex-col items-center justify-center w-full h-full p-4">
            
            <div className="w-full max-w-[420px] max-h-full bg-white/95 backdrop-blur-sm rounded-[1.5rem] shadow-2xl border border-slate-100/80 overflow-y-auto overflow-x-hidden relative transition-all ease-out animate-form-fade-in">
                
                <div className="p-4 sm:p-6 flex flex-col justify-center h-full">
                    <Logo 
                        theme="light" 
                        size="large" 
                        className="mb-4 sm:mb-6 mt-4" 
                        showSubtitle 
                        subtitleText="Recupere o acesso à sua conta." 
                    />

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-1.5">
                            <label htmlFor="email" className="text-sm font-bold text-slate-700">E-mail</label>
                            <input 
                                id="email" 
                                type="email" 
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                                className="w-full px-4 py-2.5 border bg-slate-50 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400 font-medium text-sm border-slate-200 focus:border-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed"
                                placeholder="voce@empresa.com"
                                required
                            />
                        </div>
                        
                        {mensagem && (
                            <div className="text-emerald-700 text-sm bg-emerald-50 p-3 rounded-xl border border-emerald-100 font-medium animate-in fade-in slide-in-from-top-1 text-center">
                                {mensagem}
                            </div>
                        )}
                        
                        {erro && (
                            <div className="text-red-700 text-sm bg-red-50 p-3 rounded-xl border border-red-100 font-medium animate-in fade-in slide-in-from-top-1 text-center">
                                {erro}
                            </div>
                        )}

                        <Button 
                            type="submit" 
                            fullWidth 
                            loading={loading} 
                            disabled={loading} 
                            className="py-2.5 text-sm font-bold shadow-lg shadow-indigo-600/20 rounded-xl mt-2"
                        >
                            {loading ? 'Enviando...' : 'Enviar link'}
                        </Button>
                    </form>

                    <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col items-center">
                        <Link 
                            to="/login" 
                            className="group flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl font-bold transition-all duration-300 bg-transparent text-slate-600 border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                        >
                            <ArrowLeft size={18} className="transition-transform duration-300 group-hover:-translate-x-1" />
                            Voltar para o login
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </AnimatedLoginBackground>
  );
};
